import { RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { SalesTableState } from '@/components/sales/SalesTableState';
import { SectionCard } from '@/components/sales/SectionCard';
import { StatusChip, type ChipTone } from '@/components/sales/StatusChip';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import usePersistedState from '@/hooks/usePersistedState';
import { useRenewalDashboard } from '@/hooks/useRenewals';
import { inr } from '@/lib/sales-metrics';
import { ROUTES, type RenewalStatusValue } from '@/utils/constants';

// Funnel bar color per status bucket, in the server's ladder order (mirrors the
// BillingDashboardPage CSS-width bar idiom - the repo's sanctioned chart style).
const FUNNEL_COLORS: Record<RenewalStatusValue, string> = {
  'Pending Verification': '#2f7ff0',
  'Client Details Confirmed': '#4a90d9',
  'Renewal Notice Scheduled': '#5b6b8c',
  'Renewal Notice Sent': '#8b78e6',
  'Follow-up 1 Sent': '#f6c453',
  'Follow-up 2 Sent': '#f2994a',
  'Invoice Pending': '#f28b82',
  'Service Notification Pending': '#38bdf8',
  'Client Invoice Pending': '#6366f1',
  'Renewal Complete': '#22c39a',
  'Service Terminated': '#ef4444',
  // Red like a termination, but darker: the service is off either way, and the
  // difference is that this one can be switched back on.
  Deactivated: '#b91c1c',
  // Grey: closed without ever entering the flow (Non-ARN clients, ADKJ-117).
  Skipped: '#9ca3af',
};

// Stacked per-product bar segments (pending / renewed / lost).
const PRODUCT_SEGMENTS = [
  { key: 'pendingCount', label: 'Pending', color: '#f6c453' },
  { key: 'renewedCount', label: 'Renewed', color: '#22c39a' },
  { key: 'cancelledCount', label: 'Cancelled', color: '#ef4444' },
] as const;

const statusTone = (s: RenewalStatusValue): ChipTone => {
  if (s === 'Renewal Complete') return 'success';
  if (s === 'Service Terminated') return 'destructive';
  if (s === 'Pending Verification') return 'warning';
  return 'info';
};

const fmtDate = (iso: string): string => {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  return new Date(iso).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

// Product filter sentinel - the Radix Select needs a non-empty value per option.
const ALL_PRODUCTS = 'all';

const CURRENT_YEAR = new Date().getFullYear();
// Current year plus a few around it (renewal cycles look back and ahead).
const YEAR_OPTIONS = [
  CURRENT_YEAR - 3,
  CURRENT_YEAR - 2,
  CURRENT_YEAR - 1,
  CURRENT_YEAR,
  CURRENT_YEAR + 1,
];

const RenewalDashboardPage = () => {
  const navigate = useNavigate();
  const [product, setProduct] = usePersistedState<string>('renewalDashboard:product', ALL_PRODUCTS);
  const [year, setYear] = useState<number>(CURRENT_YEAR);

  const dashboardQuery = useRenewalDashboard({
    productCode: product === ALL_PRODUCTS ? undefined : product,
    year,
  });
  const { isLoading } = dashboardQuery;
  const dashboard = dashboardQuery.data;

  const funnel = dashboard?.statusFunnel ?? [];
  const funnelMax = Math.max(1, ...funnel.map((f) => f.count));
  const byProduct = dashboard?.byProduct ?? [];
  const monthly = dashboard?.monthly ?? [];
  const upcoming = dashboard?.upcoming ?? [];

  const yearTotals = monthly.reduce(
    (acc, row) => ({
      due: acc.due + row.due,
      renewed: acc.renewed + row.renewed,
      collected: acc.collected + row.collected,
      pending: acc.pending + row.pending,
    }),
    {
      due: 0,
      renewed: 0,
      collected: 0,
      pending: 0,
    },
  );

  const goWorkflow = (id: number) => {
    void navigate(ROUTES.RENEWAL_WORKFLOW(id));
  };

  if (dashboardQuery.isError) {
    return (
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="text-lg font-bold text-foreground">Renewal Dashboard</h1>
          <p className="text-sm text-muted-foreground">
            Team-wide analytics for the renewals pipeline.
          </p>
        </div>
        <SalesTableState
          variant="error"
          title="Couldn't load renewal analytics"
          onRetry={() => {
            void dashboardQuery.refetch();
          }}
          className="min-h-[320px] rounded-xl border border-border bg-card"
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold text-foreground">Renewal Dashboard</h1>
          <p className="text-sm text-muted-foreground">
            Team-wide analytics for the renewals pipeline.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            void dashboardQuery.refetch();
          }}
          disabled={dashboardQuery.isFetching}
        >
          <RefreshCw
            className={`mr-1.5 h-3.5 w-3.5 ${dashboardQuery.isFetching ? 'animate-spin' : ''}`}
          />
          Refresh
        </Button>
      </div>

      {/* Filters - product scopes the funnel/monthly/upcoming blocks (the
          by-product table is always the full breakdown); year drives Monthly. */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="grid gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Product
          </span>
          <Select
            value={product}
            onChange={(e) => setProduct(e.target.value)}
            className="h-9 w-56"
            aria-label="Filter by product"
          >
            <option value={ALL_PRODUCTS}>All products</option>
            {byProduct.map((p) => (
              <option key={p.productCode} value={p.productCode}>
                {p.productName}
              </option>
            ))}
          </Select>
        </div>
        <div className="grid gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Year
          </span>
          <Select
            value={String(year)}
            onChange={(e) => setYear(Number(e.target.value))}
            className="h-9 w-32"
            aria-label="Filter by year"
          >
            {YEAR_OPTIONS.map((y) => (
              <option key={y} value={String(y)}>
                {y}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {/* Funnel + by-product side by side */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Status funnel - one bar per status bucket, each renewal counted once
            in its current stage. */}
        <SectionCard title="Status Funnel" className="h-full">
          {isLoading ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Loading funnel…</p>
          ) : (
            <div className="space-y-2.5 py-1">
              {funnel.map((f) => (
                <div
                  key={f.status}
                  className="grid grid-cols-[130px_1fr_auto] items-center gap-2 sm:grid-cols-[190px_1fr_auto]"
                >
                  <span className="truncate text-xs font-semibold text-foreground" title={f.status}>
                    {f.status}
                  </span>
                  <div className="h-4 w-full overflow-hidden rounded-full bg-secondary">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${(f.count / funnelMax) * 100}%`,
                        background: FUNNEL_COLORS[f.status],
                      }}
                    />
                  </div>
                  <span className="w-6 text-right text-sm font-extrabold tabular-nums text-foreground">
                    {f.count}
                  </span>
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        {/* Renewals by Product - how many renewals are left (pending) per
            product, next to renewed / lost. Always the full breakdown. */}
        <SectionCard title="Renewals by Product" className="h-full">
          {isLoading && (
            <p className="py-8 text-center text-sm text-muted-foreground">Loading products…</p>
          )}
          {!isLoading && byProduct.length === 0 && (
            <p className="py-8 text-center text-sm text-muted-foreground">No renewals yet.</p>
          )}
          {!isLoading && byProduct.length > 0 && (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead className="w-32"> </TableHead>
                      <TableHead className="text-right">Pending</TableHead>
                      <TableHead className="text-right">Renewed</TableHead>
                      <TableHead className="text-right">Cancelled</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {byProduct.map((p) => {
                      const total = Math.max(1, p.pendingCount + p.renewedCount + p.cancelledCount);
                      return (
                        <TableRow key={p.productCode} className="whitespace-nowrap">
                          <TableCell className="font-semibold">{p.productName}</TableCell>
                          <TableCell>
                            <div className="flex h-2.5 w-28 overflow-hidden rounded-full bg-secondary">
                              {PRODUCT_SEGMENTS.map((seg) => (
                                <div
                                  key={seg.key}
                                  title={`${seg.label}: ${p[seg.key]}`}
                                  style={{
                                    width: `${(p[seg.key] / total) * 100}%`,
                                    background: seg.color,
                                  }}
                                />
                              ))}
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-extrabold tabular-nums text-warning">
                            {p.pendingCount}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-success">
                            {p.renewedCount}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-destructive">
                            {p.cancelledCount}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                {PRODUCT_SEGMENTS.map((seg) => (
                  <span
                    key={seg.key}
                    className="flex items-center gap-1.5 text-[11px] text-muted-foreground"
                  >
                    <span className="h-2 w-2 rounded-full" style={{ background: seg.color }} />
                    {seg.label}
                  </span>
                ))}
              </div>
            </>
          )}
        </SectionCard>
      </div>

      {/* Monthly view for the selected year */}
      <SectionCard title={`Monthly (${year})`}>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Month</TableHead>
                <TableHead className="text-right">Due</TableHead>
                <TableHead className="text-right">Renewed</TableHead>
                <TableHead className="text-right">Collected</TableHead>
                <TableHead className="text-right">Pending</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                    Loading…
                  </TableCell>
                </TableRow>
              )}
              {!isLoading &&
                monthly.map((row) => (
                  <TableRow key={row.month} className="whitespace-nowrap">
                    <TableCell className="font-semibold">{row.month}</TableCell>
                    <TableCell className="text-right tabular-nums">{row.due}</TableCell>
                    <TableCell className="text-right tabular-nums text-success">
                      {row.renewed}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{inr(row.collected)}</TableCell>
                    <TableCell className="text-right tabular-nums text-warning">
                      {row.pending}
                    </TableCell>
                  </TableRow>
                ))}
              {!isLoading && monthly.length > 0 && (
                <TableRow className="whitespace-nowrap border-t-2 border-border">
                  <TableCell className="font-extrabold">Total</TableCell>
                  <TableCell className="text-right font-extrabold tabular-nums">
                    {yearTotals.due}
                  </TableCell>
                  <TableCell className="text-right font-extrabold tabular-nums text-success">
                    {yearTotals.renewed}
                  </TableCell>
                  <TableCell className="text-right font-extrabold tabular-nums">
                    {inr(yearTotals.collected)}
                  </TableCell>
                  <TableCell className="text-right font-extrabold tabular-nums text-warning">
                    {yearTotals.pending}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </SectionCard>

      {/* Upcoming renewals - next 10 by due date, team-wide */}
      <SectionCard title="Upcoming Renewals">
        {isLoading && <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>}
        {!isLoading && upcoming.length === 0 && (
          <SalesTableState
            variant="empty"
            title="No upcoming renewals"
            description="Nothing is due from today onward for this filter."
            className="min-h-[160px]"
          />
        )}
        {!isLoading && upcoming.length > 0 && (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Renewal</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Due</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {upcoming.map((r) => (
                  <TableRow
                    key={r.id}
                    className="cursor-pointer whitespace-nowrap"
                    onClick={() => goWorkflow(r.id)}
                  >
                    <TableCell className="font-medium">{r.renewalCode}</TableCell>
                    <TableCell>
                      <p className="font-semibold">{r.clientName}</p>
                      <p className="text-xs text-muted-foreground">{r.arn}</p>
                    </TableCell>
                    <TableCell>{r.productName}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {r.amount == null ? '-' : inr(r.amount)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{fmtDate(r.dueDate)}</TableCell>
                    <TableCell>
                      <StatusChip tone={statusTone(r.status)}>{r.status}</StatusChip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </SectionCard>
    </div>
  );
};

export default RenewalDashboardPage;
